import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CssBaseline from '@mui/material/CssBaseline';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import Modal from '@mui/material/Modal';
import '@fontsource/roboto/300.css';
import API_CONSTANTS from '../resources/constants/api.json';
import { useTheme } from '@mui/material/styles';

// CUSTOM
import { VPIcon } from '../resources/Icons';
import ColorModeSelect from '../shared-theme/ColorModeSelect';
import OTPEntry from './OTPEntry';
import Colors from '../resources/constants/Colors.json';

const Card = styled(MuiCard)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    [theme.breakpoints.up('sm')]: {
        maxWidth: '450px',
    },
    boxShadow:
        'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
    ...theme.applyStyles('dark', {
        boxShadow:
            'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
    }),
}));

const LoginContainer = styled(Stack)(({ theme }) => ({
    minHeight: '100%',
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(4),
    },
    '&::before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        zIndex: -1,
        inset: 0,
        backgroundImage:
            'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
        backgroundRepeat: 'no-repeat',
        ...theme.applyStyles('dark', {
            backgroundImage:
                'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
        }),
    },
}));

export default function Login(props) {
    const navigate = useNavigate();
    const [emailError, setEmailError] = React.useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
    const [emailValue, setEmailValue] = React.useState('');
    const [open, setOpen] = React.useState(false);
    const [responseStatus,setResponseStatus] = React.useState();
    const [isDev, setIsDev] = React.useState(false);
    const [backVersion, setBackVersion] = React.useState('0');
    const theme = useTheme();

    const handleClickOpen = () => {
        let email = emailValue.toLowerCase()
        if ( email === 'skip' && isDev) {
            navigate('/Dashboard',{state: [
                {_id: 1,
                email: 'tbaker@lightsideit.com',
                is_active:1,
                admin: 1,
                can_approve: 1,
                can_deliver: 1
            }]

            });
            return
        
        }
        let validEmail = /\w{1,100}@\w{1,100}\.{1}\w{1,10}/.test(email)

        if (validEmail) {
            setEmailError(false)
            setEmailErrorMessage('')
            fetch(`${API_CONSTANTS.API_ROUTE}/getotp?email=${emailValue}`,{
                method: 'POST',
                body: JSON.stringify({email: emailValue})
            }).then((res)=>{
                console.log(res)
                setOpen(true);
            })
        }
        else if (!validEmail) {
            setEmailError(true)
            setEmailErrorMessage('Please enter a valid email address')
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const navigateToDashboard = (user) => {
        navigate('/Dashboard',{state: user, isDev})
    }

    const catchEnterPress = (event) => {
        if (event.key === "Enter" && emailValue) {
            event.preventDefault();
            handleClickOpen();
        }
    }
    

    React.useEffect(() => {
        fetch(`${API_CONSTANTS.API_ROUTE}`,{
                method: 'GET'
            }).then(async (res) => {
                try{
                    const data = await res.json();
                    setIsDev(data?.dev);
                    setBackVersion(data?.version);
                }
                catch{
                    setIsDev(false);
                    setBackVersion('error');
                }
            })
    }, []);

    React.useEffect(() => {
        document.addEventListener("keydown", catchEnterPress, true);
    
        return () => {
            document.removeEventListener("keydown", catchEnterPress, true);
        };
        }, [catchEnterPress]);

    return (
        <>
            <CssBaseline enableColorScheme />
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="otp-modal"
                aria-describedby="modal-for-otp"
                >
                <Box sx={ {
                    position: 'absolute',
                    top: '25%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: Colors.vp_dark_white,
                    border: '2px solid #000',
                    borderRadius: 2,
                    boxShadow: 24,
                    margin: "0",
                    padding: 15,
                    textAlign: "center"
                    }}>
                    <Typography
                        variant="h3"
                        component="h3"
                        sx={{fontFamily: 'cinzel'}}
                        color={"red"}
                        >
                        {responseStatus}
                    </Typography>
                    <Typography 
                        variant="h2" 
                        component="h2" 
                        sx={{fontFamily: 'cinzel'}}
                        color={"black"}
                        >
                
                        A One time password has been sent to your email, please enter it below:
                        <br /><br />
                    </Typography>
                    {open ?
                    <OTPEntry
                        emailValue={emailValue}
                        setResponseStatus={setResponseStatus}
                        navigateToDashboard={navigateToDashboard}
                    /> :
                    null}
                </Box>
            </Modal>

            
            <LoginContainer direction="column" justifyContent="space-between">
                <ColorModeSelect sx={{ position: 'fixed', top: '1rem', right: '1rem' }} isDev={isDev}/>
                <Card variant="outlined">
                    <VPIcon />
                    <Typography
                        component="h1"
                        variant="h4"
                        sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
                    >
                        Sign in
                    </Typography>
                    <Box
                        component="form"
                        // onSubmit={handleSubmit}
                        noValidate
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            gap: 2,
                        }}
                    >
                        <FormControl>
                            <FormLabel htmlFor="email">Email</FormLabel>
                            <TextField
                                error={emailError}
                                helperText={emailErrorMessage}
                                onChange={(event)=>{setEmailValue(event.target.value.toLowerCase())}}
                                value={emailValue}
                                id="email"
                                type="email"
                                name="email"
                                placeholder="your@email.com"
                                autoComplete="email"
                                autoFocus
                                required
                                fullWidth
                                variant="outlined"
                                color={emailError ? 'error' : 'primary'}
                                sx={{ ariaLabel: 'email' }}
                            />
                        </FormControl>
                        <Button
                            disabled={!emailValue}
                            fullWidth
                            variant="contained"
                            // type="submit"
                            style={!emailValue ? {display: "none"} : null}
                            onClick={()=>{handleClickOpen()}}
                        >
                            Sign in
                        </Button>
                    </Box>
                </Card>
                <div style={{position:'absolute',bottom:0}}>
                    <Typography variant={'body2'}>
                        b{backVersion}
                    </Typography>                    
                </div>
            </LoginContainer>
        </>
    );
}